import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, firstImageProperty } from '@shapeable/utils';
import { EntityBubbleImage, EntityCard, EntityCardDefaultProps, EntityCardProps, EntityImage, OrganisationCardImage } from '@shapeable/ui';
import { Space } from '@shapeable/doing-co-types';

const cls = classNames('space-card');

// -------- Types -------->

// -------- Props -------->

export type SpaceCardProps = Omit<EntityCardProps, 'entity'> & { 
  entity?: Space;
};

export const SpaceCardDefaultProps: Omit<SpaceCardProps, 'entity'> = {
  ...EntityCardDefaultProps,
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

const ImageStyles = breakpoints({
  base: css`

  `,
});

const BuubleImageStyles = breakpoints({
  base: css`
    padding: ${theme.UNIT(4)} 0 0 ${theme.UNIT(4)};
  `,
});



const LocationLabelStyles = breakpoints({
  base: css`
    display: flex;
    ${theme.H_DOT_BG()};
    padding-top: ${theme.UNIT(2)};
    color: ${theme.COLOR('text')};
  `,
});

// -------- Components -------->

const My = {
  Container: styled(EntityCard)<ContainerProps>`${ContainerStyles}`,
    LogoImage: styled(OrganisationCardImage)`${ImageStyles}`,
    BannerImage: styled(EntityImage)`${ImageStyles}`,
    BubbleImage: styled(EntityBubbleImage)`${BuubleImageStyles}`,
    LocationLabel: styled.span`${LocationLabelStyles}`,
};

export const SpaceCard: Shapeable.FC<SpaceCardProps> = (props) => {
  const { className, entity } = props;

  const { locations = [] } = entity || {};

  const hasLocation = locations.length > 0;
  const locationName = locations[0]?.name;

  const logoImagePaths = [
    'logo.thumbnails.medium',
    'logo',
  ];

  const bannerImagePaths = [
    'banner.image',
    'banner.thumbnail',
    'banners.0.image',
    'banners.0.thumbnail',
  ];

  const logoImage = firstImageProperty(entity, logoImagePaths);
  const bannerImage = firstImageProperty(entity, bannerImagePaths);

  // Required 3 seperate components (Karmen and Scott wanted logo first, then image and finally banner fallbacks) 
  // here to ensure correct aspect ratios for each image type, 
  // and to ensure the fallback bubble image is correctly padded (as it contains an icon in the center, 
  // rather than being a full bleed image like the banner and logo)
  // Issues with styling and logo fitting as well as supporting banner and then a logo
  const image: React.ReactNode = !!logoImage
    ? <My.LogoImage entity={entity} />
    : !!bannerImage
      ? <My.BannerImage entity={entity} paths={bannerImagePaths} />
      : <My.BubbleImage size={96} alwaysShowIcon iconColors={{ fill: 'primary', stroke: 'primary' }} entity={entity} />;

  return (
    <My.Container 
    className={cls.name(className)} 
    entity={entity}
    image={image}
    showImage={true}
    labelDeriver={() => entity?.type?.name}
    foot={
      hasLocation && <My.LocationLabel>{locationName}{!!locations[0]?.state && ', '}{locations[0]?.state?.abbr || locations[0]?.state?.name}</My.LocationLabel>
    }

    />
  )
};

SpaceCard.defaultProps = SpaceCardDefaultProps;
SpaceCard.cls = cls;